import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { CheckCircle, Loader, XCircle } from 'lucide-react';

export default function VerifyTrial() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('กำลังยืนยันอีเมล...');
  const [trialDays, setTrialDays] = useState<number>(7);

  useEffect(() => {
    const verifyTrial = async () => {
      const product = (searchParams.get('product') || 'salepage').toLowerCase();
      const uid = searchParams.get('uid') || searchParams.get('userId');
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');

      if (mode === 'verifyEmail' && oobCode) {
        try {
          await applyActionCode(auth, oobCode);
        } catch (error) {
          console.error('Error applying action code:', error);
          setStatus('error');
          setMessage('ลิงก์ยืนยันอีเมลไม่ถูกต้องหรือหมดอายุ กรุณาลองส่งอีเมลยืนยันใหม่');
          return;
        }
      }

      if (!uid) {
        setStatus('error');
        setMessage('ลิงก์ไม่ถูกต้อง');
        return;
      }

      try {
        const now = Timestamp.now();
        const effectiveTrialDays = product === 'course' ? 14 : 7;
        setTrialDays(effectiveTrialDays);
        const trialEnd = Timestamp.fromDate(new Date(Date.now() + effectiveTrialDays * 24 * 60 * 60 * 1000));

        if (product === 'salepage') {
          const subRef = doc(db, 'salePageSubscriptions', uid);
          const existing = await getDoc(subRef);

          const base = existing.exists() ? existing.data() : {};

          await setDoc(
            subRef,
            {
              userId: uid,
              email: (base as any).email || '',
              status: 'trial',
              plan: 'free',
              trialStartDate: now,
              trialEndDate: trialEnd,
              isTrialUsed: true,
              hasPathUrl: (base as any).hasPathUrl || false,
              hasSubdomain: (base as any).hasSubdomain || false,
              username: (base as any).username || null,
              lastPaymentDate: null,
              lastPaymentAmount: null,
              createdAt: now,
              updatedAt: now,
            }
          );

          // Add trial start record to payment history
          const { addDoc, collection } = await import('firebase/firestore');
          await addDoc(collection(db, 'salePagePaymentNotifications'), {
            userId: uid,
            email: (base as any).email || '',
            plan: 'free',
            amount: 0,
            status: 'verified',
            type: 'trial_start',
            createdAt: now,
            verifiedAt: now,
            note: 'เริ่มทดลองใช้ฟรี 7 วัน'
          });
        } else if (product === 'course') {
          const userRef = doc(db, 'users', uid);
          const existing = await getDoc(userRef);
          const base = existing.exists() ? existing.data() : {};

          await setDoc(
            userRef,
            {
              ...base,
              trialStartedAt: (base as any).trialStartedAt || now,
              trialExpired: false,
              courseTrialStatus: 'trial',
              updatedAt: now,
            },
            { merge: true }
          );
        }

        setStatus('success');
        setMessage(product === 'course' ? 'ยืนยันอีเมลสำเร็จ! เริ่มทดลองใช้ฟรี 14 วัน' : 'ยืนยันอีเมลสำเร็จ! เริ่มทดลองใช้ฟรี 7 วัน');

        setTimeout(() => {
          navigate(product === 'course' ? '/instructor/dashboard' : '/salepage/dashboard');
        }, 2000);

      } catch (error) {
        console.error('Error verifying trial:', error);
        setStatus('error');
        setMessage('เกิดข้อผิดพลาด กรุณาติดต่อทีมงาน');
      }
    };

    verifyTrial();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <Loader className="w-16 h-16 text-green-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">กำลังยืนยันอีเมล</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">ยืนยันสำเร็จ!</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
              <p className="text-green-800 font-semibold">🎉 ทดลองใช้ฟรี {trialDays} วัน</p>
              <p className="text-sm text-green-700 mt-1">เริ่มนับตั้งแต่วันนี้</p>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
            >
              กลับหน้าแรก
            </button>
          </>
        )}
      </div>
    </div>
  );
}
